import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FolderOpen, Plus, X } from "lucide-react";
import FolderPickerModal from "./FolderPickerModal";

/**
 * Sub-locations under a subdomain.
 *
 * The vhost's own `location /` goes to the app. These are the extra paths
 * beneath it: send /api to another port, serve /docs off the disk.
 *
 * "Trim the prefix" is one toggle covering both target types, because in
 * nginx it is the same idea expressed two ways:
 *
 *   port,   trim off → proxy_pass http://127.0.0.1:9102;   backend sees /api/users
 *   port,   trim on  → proxy_pass http://127.0.0.1:9102/;  backend sees /users
 *   folder, trim off → root /srv;        serves /srv/docs/x
 *   folder, trim on  → alias /srv/docs/; serves /srv/x
 *
 * Rows round-trip: the server writes a marker comment above each generated
 * block and reads it back, so re-opening the editor shows these rows again.
 * Locations written by hand in the file carry no marker and do NOT appear
 * here — they are preserved on save, but edit them in the raw editor.
 */
const blank = () => ({ path: "", type: "port", host: "", port: "", folder: "", strip: true, spa: false });

export default function LocationsEditor({ value = [], onChange, projectId, serverId, servers = [] }) {
  const [pickFor, setPickFor] = useState(null); // row index whose folder is being picked

  const rows = Array.isArray(value) ? value : [];
  const set = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <>
      <Form.Label className="mb-1">
        Sub-locations <small className="text-muted">(optional)</small>
      </Form.Label>
      <div className="small text-muted mb-2">
        Paths under this domain that go somewhere other than the app — another
        port, or a folder on disk. Everything else still goes to the upstream above.
      </div>

      {rows.map((r, i) => (
        <div key={i} className="p-2 mb-2 rounded" style={{ border: "1px solid var(--line, #e2e8f0)" }}>
          <div className="d-flex gap-2 mb-2">
            <Form.Control
              size="sm" placeholder="/api" value={r.path}
              style={{ fontFamily: "ui-monospace, monospace", maxWidth: 180 }}
              onChange={(e) => set(i, { path: e.target.value })}
            />
            <Form.Select
              size="sm" value={r.type} style={{ maxWidth: 130 }}
              onChange={(e) => set(i, { type: e.target.value })}
            >
              <option value="port">→ Port</option>
              <option value="folder">→ Folder</option>
            </Form.Select>

            {r.type === "port" ? (
              <>
                {/* Host, then port. Blank means loopback, which is right when
                    the app runs beside nginx; on a jumphost fronting internal
                    servers this is the internal address instead. */}
                <Form.Control
                  size="sm" list="loc-hosts" placeholder="127.0.0.1" value={r.host || ""}
                  style={{ fontFamily: "ui-monospace, monospace", maxWidth: 150 }}
                  onChange={(e) => set(i, { host: e.target.value })}
                />
                <Form.Control
                  size="sm" type="number" min={1} max={65535} placeholder="9102" value={r.port}
                  style={{ fontFamily: "ui-monospace, monospace", maxWidth: 100 }}
                  onChange={(e) => set(i, { port: e.target.value })}
                />
              </>
            ) : (
              <div className="d-flex gap-1 flex-grow-1">
                <Form.Control
                  size="sm" placeholder="/var/www/docs" value={r.folder}
                  style={{ fontFamily: "ui-monospace, monospace" }}
                  onChange={(e) => set(i, { folder: e.target.value })}
                />
                <Button size="sm" variant="light" title="Browse the server"
                  onClick={() => setPickFor(i)}>
                  <FolderOpen size={14} />
                </Button>
              </div>
            )}

            <Button size="sm" variant="outline-danger" aria-label="Remove location"
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
              <X size={14} />
            </Button>
          </div>

          <div className="d-flex gap-3 flex-wrap">
            <Form.Check
              type="switch" id={`strip-${i}`} checked={!!r.strip}
              onChange={(e) => set(i, { strip: e.target.checked })}
              label={<span className="small">Trim <code>{r.path || "/path"}</code> from the forwarded path</span>}
            />
            {r.type === "folder" && (
              <Form.Check
                type="switch" id={`spa-${i}`} checked={!!r.spa}
                onChange={(e) => set(i, { spa: e.target.checked })}
                label={<span className="small">Single-page app (fall back to <code>index.html</code>)</span>}
              />
            )}
          </div>

          {/* Shows the consequence in the terms the operator cares about,
              rather than making them recall nginx's trailing-slash rule. */}
          <div className="small text-muted mt-1">
            {r.type === "port"
              ? <>A request to <code>{(r.path || "/path").replace(/\/$/, "")}/users</code> reaches{" "}
                <code>{(r.host || "127.0.0.1")}:{r.port || "…"}</code> as{" "}
                <code>{r.strip ? "/users" : `${(r.path || "/path").replace(/\/$/, "")}/users`}</code>.</>
              : <>A request to <code>{(r.path || "/path").replace(/\/$/, "")}/x.txt</code> serves{" "}
                <code>{r.strip
                  ? `${(r.folder || "/folder").replace(/\/$/, "")}/x.txt`
                  : `${(r.folder || "/folder").replace(/\/$/, "")}${(r.path || "/path").replace(/\/$/, "")}/x.txt`}</code>.</>}
          </div>
        </div>
      ))}

      {/* Suggestions only — a host that isn't a project server is still
          allowed, since an app may sit on a box the portal doesn't manage. */}
      <datalist id="loc-hosts">
        <option value="127.0.0.1">this server</option>
        {servers.map((sv) => (
          <option key={sv._id} value={sv.host}>{sv.label}</option>
        ))}
      </datalist>

      <Button size="sm" variant="light" onClick={() => onChange([...rows, blank()])}>
        <Plus size={14} className="me-1" /> Add sub-location
      </Button>

      {pickFor !== null && (
        <FolderPickerModal
          projectId={projectId}
          serverId={serverId}
          initialPath={rows[pickFor]?.folder || "/var/www"}
          onPick={(p) => set(pickFor, { folder: p })}
          onClose={() => setPickFor(null)}
        />
      )}
    </>
  );
}
